import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";

import { useResolvedHas } from "~/feature/subscription/hooks/useResolvedHas";
import { FeatureKey } from "~/types/subscription";

const allSubscriptionPermissions = Object.values(FeatureKey);

interface IAppUserProviderProps {
  children: ReactNode;
}

interface IAppUserContextType {
  userSubscriptionPermissions: FeatureKey[];
  syncLoading: boolean;
  setSyncLoading: (value: boolean) => void;
}

const AppUserContext = createContext<IAppUserContextType>({
  userSubscriptionPermissions: [],
  syncLoading: false,
  setSyncLoading: () => {},
});

export const AppUserProvider = ({ children }: IAppUserProviderProps) => {
  const { hasFeature } = useResolvedHas();
  const [syncLoading, setSyncLoading] = useState(false);

  const userSubscriptionPermissions = useMemo(
    () => allSubscriptionPermissions.filter((item) => hasFeature(item)),
    [hasFeature]
  );

  const value = useMemo(
    () => ({
      userSubscriptionPermissions,
      syncLoading,
      setSyncLoading,
    }),
    [syncLoading, userSubscriptionPermissions]
  );

  return (
    <AppUserContext.Provider value={value}>{children}</AppUserContext.Provider>
  );
};

export const useAppUser = (): IAppUserContextType => {
  const context = useContext(AppUserContext);

  if (!context) {
    throw new Error("useAppUser must be used within a AppUserProvider");
  }

  return context;
};
